import { useEffect, useState } from "react";
import AnimateHeight from "react-animate-height";
import PerfectScrollbar from "react-perfect-scrollbar";
import { useDispatch, useSelector } from "react-redux";
import { NavLink } from "react-router-dom";
import { ReactComponent as IconAccountCustomer } from "../../assets/icons/ic_account_customer.svg";
import { ReactComponent as IconBackground } from "../../assets/icons/ic_background.svg";
import { ReactComponent as IconBooking } from "../../assets/icons/ic_booking.svg";
import { ReactComponent as IconBranch } from "../../assets/icons/ic_branch.svg";
import { ReactComponent as IconCashCoin } from "../../assets/icons/ic_cash_coin.svg";
import { ReactComponent as IconDashboard } from "../../assets/icons/ic_dashboard.svg";
import { ReactComponent as IconEmployee } from "../../assets/icons/ic_employee.svg";
import { ReactComponent as IconGame } from "../../assets/icons/ic_game.svg";
import { ReactComponent as IcGeneralSettings } from "../../assets/icons/ic_general_settings.svg";
import { ReactComponent as IconApiKey } from "../../assets/icons/ic_lock_dots.svg";
import { ReactComponent as IconNewFeed } from "../../assets/icons/ic_newfeed.svg";
import { ReactComponent as IconPcDisplay } from "../../assets/icons/ic_pc_display.svg";
import { ReactComponent as IconPcDisplayHorizontal } from "../../assets/icons/ic_pc_display_horizontal.svg";
import { ReactComponent as IconPrinter } from "../../assets/icons/ic_printer.svg";
import { ReactComponent as IconService } from "../../assets/icons/ic_service.svg";
import { ReactComponent as IconShop } from "../../assets/icons/ic_shop.svg";
import { ReactComponent as IconTicket } from "../../assets/icons/ic_ticket.svg";

import logo from "../../assets/images/logo.png";
import { COOKIE_KEYS } from "../../constants/cookie-key";
import { PRIVILEGE_CODES } from "../../constants/privileges";
import { PROMOTION_CAMPAIGN_TYPE } from "../../constants/status";
import { STRING_MENU, STRING_SUB_MENU } from "../../constants/string";
import { useFetchData } from "../../hooks/useFetch";
import { PromotionSubMenuConfig } from "../../models/response/promotions/promotion-sub-menu-config";
import { MODULE_ROUTE } from "../../routers/module-router";
import { API_BRAND_ROUTER, ApiConfig } from "../../services/api-config";
import { IRootState } from "../../store";
import {
  setSidebarMenu,
  setSidebarMenuSub,
  toggleSidebar,
} from "../../store/themeConfigSlice";
import CookieUtils from "../../utils/cookie-utils";
import { usePrivilegeCodes } from "../../utils/helpers";
import IconCaretsDown from "../icons/icon-carets-down";
import { IconHistory } from "../icons/icon-group";
import MenuItem from "./menu-item";
import RotatingIcon from "./rotating-icon";

const Sidebar = () => {
  const dispatch = useDispatch();

  const [currentMenu, setCurrentMenu] = useState<string>("");
  const [subMenu, setSubMenu] = useState("");
  const sidebarMenu = localStorage.getItem("sidebarMenu");
  const sidebarMenuSub = localStorage.getItem("sidebarMenuSub");
  const semidry = useSelector(
    (state: IRootState) => state.themeConfig.semidark
  );
  const privilegeCodes = usePrivilegeCodes();
  const [promotionSubMenuConfig, setPromotionSubMenuConfig] = useState<
    PromotionSubMenuConfig[]
  >([]);
  const [showPromotionSubMenuConfig, setShowPromotionSubMenuConfig] =
    useState<boolean>(false);

  const {
    request: getPromotionSubMenuConfig,
    baseResponse: promotionSubMenuConfigRes,
  } = useFetchData.Get<PromotionSubMenuConfig[]>({
    projectId: ApiConfig.PROJECT_ID.USER_SERVICE,
  });

  const toggleMenu = (value: string) => {
    dispatch(setSidebarMenu(value));
    setCurrentMenu((oldValue) => {
      return oldValue === value ? "" : value;
    });
    setSubMenu("");
  };

  useEffect(() => {
    dispatch(setSidebarMenuSub(subMenu));
  }, [subMenu]);

  useEffect(() => {
    if (!sidebarMenu || sidebarMenu === "") return;
    setCurrentMenu(sidebarMenu);
  }, [sidebarMenu]);

  useEffect(() => {
    if (!sidebarMenuSub || sidebarMenuSub === "") return;
    setSubMenu(sidebarMenuSub);
  }, [sidebarMenuSub]);

  useEffect(() => {
    const fetchPromotionSubMenuConfig = async () => {
      await getPromotionSubMenuConfig({
        endPoint: API_BRAND_ROUTER.API_GET_PROMOTIONS_SUB_MENU_CONFIG,
        data: {},
      });
    };
    if (
      privilegeCodes.find(
        (item) =>
          item === PRIVILEGE_CODES.OWNER_MANAGEMENT ||
          item === PRIVILEGE_CODES.PROMOTION_COMBO_MANAGEMENT ||
          item === PRIVILEGE_CODES.PROMOTION_TICKET_MANAGEMENT ||
          item === PRIVILEGE_CODES.PROMOTION_RECHARGE_PACKAGE_MANAGEMENT ||
          item === PRIVILEGE_CODES.PROMOTION_CUSTOMER_DISCOUNT_MANAGEMENT ||
          item === PRIVILEGE_CODES.PROMOTION_MEMBERSHIP_LEVEL_MANAGEMENT ||
          item === PRIVILEGE_CODES.PROMOTION_AREA_DISCOUNT_MANAGEMENT ||
          item === PRIVILEGE_CODES.PROMOTION_CREDIT_MANAGEMENT
      )
    ) {
      fetchPromotionSubMenuConfig();
    }
  }, []);

  useEffect(() => {
    if (!promotionSubMenuConfigRes) return;
    if (promotionSubMenuConfigRes?.data) {
      setPromotionSubMenuConfig(promotionSubMenuConfigRes.data);
      setShowPromotionSubMenuConfig(promotionSubMenuConfig.length > 0);

      CookieUtils.setCookie(
        COOKIE_KEYS.PROMOTION_SUB_MENU_CONFIG,
        JSON.stringify(promotionSubMenuConfigRes?.data)
      );
    }
  }, [promotionSubMenuConfigRes, promotionSubMenuConfig]);

  const MENU_CONFIG = {
    [PROMOTION_CAMPAIGN_TYPE.COMBO]: {
      route: MODULE_ROUTE.PROMOTION_COMBO_PAGE,
    },
    [PROMOTION_CAMPAIGN_TYPE.TICKET]: {
      route: MODULE_ROUTE.PROMOTION_TICKET_PAGE,
    },
    [PROMOTION_CAMPAIGN_TYPE.CUSTOMER_DISCOUNT]: {
      route: MODULE_ROUTE.PROMOTION_CUSTOMER_DISCOUNT_PAGE,
    },
    [PROMOTION_CAMPAIGN_TYPE.MEMBERSHIP_LEVEL]: {
      route: MODULE_ROUTE.PROMOTION_MEMBERSHIP_LEVEL_PAGE,
    },
    [PROMOTION_CAMPAIGN_TYPE.RECHARGE_PACKAGE_MILESTONES]: {
      route: MODULE_ROUTE.PROMOTION_RECHARGE_PACKAGE_MILESTONES_PAGE,
    },
    [PROMOTION_CAMPAIGN_TYPE.AREA_DISCOUNT]: {
      route: MODULE_ROUTE.PROMOTION_AREA_DISCOUNT_PAGE,
    },
  };

  type MenuConfigType = typeof MENU_CONFIG;
  const renderPromotionSubMenu = () => {
    return promotionSubMenuConfig
      ?.filter((item) => {
        switch (item.type) {
          case PROMOTION_CAMPAIGN_TYPE.COMBO:
            return privilegeCodes.find(
              (code) =>
                code === PRIVILEGE_CODES.OWNER_MANAGEMENT ||
                code === PRIVILEGE_CODES.PROMOTION_COMBO_MANAGEMENT
            );
          case PROMOTION_CAMPAIGN_TYPE.TICKET:
            return privilegeCodes.find(
              (code) =>
                code === PRIVILEGE_CODES.OWNER_MANAGEMENT ||
                code === PRIVILEGE_CODES.PROMOTION_TICKET_MANAGEMENT
            );

          case PROMOTION_CAMPAIGN_TYPE.RECHARGE_PACKAGE_MILESTONES:
            return privilegeCodes.find(
              (code) =>
                code === PRIVILEGE_CODES.OWNER_MANAGEMENT ||
                code === PRIVILEGE_CODES.PROMOTION_RECHARGE_PACKAGE_MANAGEMENT
            );
          case PROMOTION_CAMPAIGN_TYPE.CUSTOMER_DISCOUNT:
            return privilegeCodes.find(
              (code) =>
                code === PRIVILEGE_CODES.OWNER_MANAGEMENT ||
                code === PRIVILEGE_CODES.PROMOTION_CUSTOMER_DISCOUNT_MANAGEMENT
            );
          case PROMOTION_CAMPAIGN_TYPE.MEMBERSHIP_LEVEL:
            return privilegeCodes.find(
              (code) =>
                code === PRIVILEGE_CODES.OWNER_MANAGEMENT ||
                code === PRIVILEGE_CODES.PROMOTION_MEMBERSHIP_LEVEL_MANAGEMENT
            );
          case PROMOTION_CAMPAIGN_TYPE.AREA_DISCOUNT:
            return privilegeCodes.find(
              (code) =>
                code === PRIVILEGE_CODES.OWNER_MANAGEMENT ||
                code === PRIVILEGE_CODES.PROMOTION_AREA_DISCOUNT_MANAGEMENT
            );
          default:
            break;
        }
        return item.type && MENU_CONFIG[item.type as keyof MenuConfigType];
      })
      .map((item) => {
        const { route } = MENU_CONFIG[item.type as keyof MenuConfigType];
        return (
          <li key={item._id}>
            <NavLink
              to={route}
              onClick={() => {
                CookieUtils.setCookie(
                  COOKIE_KEYS.CURRENT_PROMOTION_SUB_MENU_CONFIG,
                  JSON.stringify(item.name)
                );
              }}
            >
              {item.name}
            </NavLink>
          </li>
        );
      });
  };

  return (
    <div className={semidry ? "dark" : ""}>
      <nav
        className={`sidebar fixed min-h-screen h-full top-0 bottom-0 w-[260px] shadow-[5px_0_25px_0_rgba(94,92,154,0.1)] z-50 transition-all duration-300 ${
          semidry ? "text-white-dark" : ""
        }`}
      >
        <div className="h-full bg-white dark:bg-black">
          <div className="flex items-center justify-between px-4 py-3">
            <NavLink to="/" className="flex items-center main-logo shrink-0">
              <div className="flex flex-col w-full">
                <div className="flex flex-row items-center gap-2">
                  <img className="h-12 rounded-full" src={logo} alt="" />
                  <span className="text-sm font-semibold text-[#E96012] dark:text-white break-all max-w-[200px]">
                    {CookieUtils.getCurrentUser().brand_name}
                  </span>
                </div>
              </div>
            </NavLink>

            <button
              type="button"
              className="flex items-center w-8 h-8 transition duration-300 rounded-full collapse-icon hover:bg-gray-500/10 dark:hover:bg-dark-light/10 dark:text-white-light rtl:rotate-180"
              onClick={() => dispatch(toggleSidebar())}
            >
              <IconCaretsDown className="m-auto rotate-90" />
            </button>
          </div>
          <PerfectScrollbar className="h-[calc(100vh-80px)] relative">
            <ul className="relative font-semibold space-y-0.5 p-4 py-0">
              {/* Quản lý dịch vụ */}
              {privilegeCodes.find(
                (item) =>
                  item === PRIVILEGE_CODES.OWNER_MANAGEMENT ||
                  item === PRIVILEGE_CODES.ORDER_MANAGEMENT ||
                  item === PRIVILEGE_CODES.TOPUP_BASIC ||
                  item === PRIVILEGE_CODES.PROMOTION_COMBO_MANAGEMENT ||
                  item === PRIVILEGE_CODES.ORDER_PENDING ||
                  item === PRIVILEGE_CODES.BOOKING_MANAGEMENT
              ) && (
                <li className="nav-item">
                  <NavLink
                    to="/"
                    className="w-full nav-link group"
                    onClick={() => toggleMenu(STRING_MENU.MANAGEMENT_SERVICE)}
                  >
                    <MenuItem
                      menu={STRING_MENU.MANAGEMENT_SERVICE}
                      icon={IconService}
                      subMenu={currentMenu}
                    />
                  </NavLink>
                </li>
              )}
              {privilegeCodes.find(
                (item) =>
                  item === PRIVILEGE_CODES.REPORT_MANAGEMENT ||
                  item === PRIVILEGE_CODES.OWNER_MANAGEMENT
              ) && (
                <li className="nav-item">
                  <NavLink
                    to={MODULE_ROUTE.DASHBOARD}
                    className="w-full nav-link group"
                    onClick={() => toggleMenu(STRING_MENU.DASHBOARD)}
                  >
                    <MenuItem
                      menu={STRING_MENU.DASHBOARD}
                      icon={IconDashboard}
                      subMenu={currentMenu}
                    />
                  </NavLink>
                </li>
              )}

              {/* Báo cáo */}
              {privilegeCodes.find(
                (item) =>
                  item === PRIVILEGE_CODES.REPORT_MANAGEMENT ||
                  item === PRIVILEGE_CODES.OWNER_MANAGEMENT
              ) && (
                <li className="menu nav-item">
                  <button
                    type="button"
                    className={`${
                      currentMenu === STRING_MENU.REPORT ? "active" : ""
                    } nav-link group w-full`}
                    onClick={() => toggleMenu(STRING_MENU.REPORT)}
                  >
                    <MenuItem
                      menu={STRING_MENU.REPORT}
                      icon={IconDashboard}
                      subMenu={currentMenu}
                    />
                    <RotatingIcon
                      menu={STRING_MENU.REPORT}
                      currentMenu={currentMenu}
                      iconType="single"
                    />
                  </button>

                  <AnimateHeight
                    duration={300}
                    height={currentMenu === STRING_MENU.REPORT ? "auto" : 0}
                  >
                    <ul className="text-gray-500 sub-menu">
                      <li>
                        <NavLink to={MODULE_ROUTE.REPORTS_DASHBOARD}>
                          {STRING_SUB_MENU.REPORTS_DASHBOARD}
                        </NavLink>
                      </li>
                      <li>
                        <NavLink to={MODULE_ROUTE.REPORT_REVENUE}>
                          {STRING_SUB_MENU.REPORT_REVENUE}
                        </NavLink>
                      </li>
                      <li>
                        <NavLink to={MODULE_ROUTE.REPORT_COMPUTER_PERFORMANCE}>
                          {STRING_SUB_MENU.REPORT_COMPUTER_PERFORMANCE}
                        </NavLink>
                      </li>
                      <li>
                        <NavLink to={MODULE_ROUTE.REPORT_WORKSTATION_STATUS}>
                          {STRING_SUB_MENU.REPORT_WORKSTATION_STATUS}
                        </NavLink>
                      </li>
                      <li>
                        <NavLink to={MODULE_ROUTE.REPORT_PRODUCT_SALES}>
                          {STRING_SUB_MENU.REPORT_PRODUCT_SALES}
                        </NavLink>
                      </li>
                      <li>
                        <NavLink to={MODULE_ROUTE.REPORT_SHIFT_CLOSE}>
                          {STRING_SUB_MENU.REPORT_SHIFT_CLOSE}
                        </NavLink>
                      </li>
                    </ul>
                  </AnimateHeight>
                </li>
              )}

              {/* Quản lý máy trạm */}
              {privilegeCodes.find(
                (item) =>
                  item === PRIVILEGE_CODES.OWNER_MANAGEMENT ||
                  item === PRIVILEGE_CODES.COMPUTER_BASIC
              ) && (
                <li className="nav-item">
                  <NavLink
                    to={MODULE_ROUTE.COMPUTERS}
                    className="w-full nav-link group"
                    onClick={() => toggleMenu(STRING_MENU.MANAGEMENT_COMPUTERS)}
                  >
                    <MenuItem
                      menu={STRING_MENU.MANAGEMENT_COMPUTERS}
                      icon={IconPcDisplayHorizontal}
                      subMenu={currentMenu}
                    />
                  </NavLink>
                </li>
              )}

              {/* Quản lý khách hàng */}
              {privilegeCodes.find(
                (item) =>
                  item === PRIVILEGE_CODES.OWNER_MANAGEMENT ||
                  item === PRIVILEGE_CODES.CUSTOMER_ACCOUNT_BASIC ||
                  item === PRIVILEGE_CODES.TOPUP_BASIC
              ) && (
                <>
                  <li className="menu nav-item">
                    <button
                      type="button"
                      className={`${
                        currentMenu === STRING_MENU.MANAGEMENT_ACCOUNT_CUSTOMER
                          ? "active"
                          : ""
                      } nav-link group w-full`}
                      onClick={() =>
                        toggleMenu(STRING_MENU.MANAGEMENT_ACCOUNT_CUSTOMER)
                      }
                    >
                      <MenuItem
                        menu={STRING_MENU.MANAGEMENT_ACCOUNT_CUSTOMER}
                        icon={IconAccountCustomer}
                        subMenu={currentMenu}
                      />
                      <RotatingIcon
                        menu={STRING_MENU.MANAGEMENT_ACCOUNT_CUSTOMER}
                        currentMenu={currentMenu}
                        iconType="single"
                      />
                    </button>

                    <AnimateHeight
                      duration={300}
                      height={
                        currentMenu === STRING_MENU.MANAGEMENT_ACCOUNT_CUSTOMER
                          ? "auto"
                          : 0
                      }
                    >
                      <ul className="text-gray-500 sub-menu">
                        {privilegeCodes.find(
                          (item) =>
                            item === PRIVILEGE_CODES.OWNER_MANAGEMENT ||
                            item === PRIVILEGE_CODES.CUSTOMER_ACCOUNT_BASIC
                        ) && (
                          <li>
                            <NavLink to={MODULE_ROUTE.LIST_ACCOUNT_CUSTOMER}>
                              {STRING_SUB_MENU.LIST_ACCOUNT_CUSTOMER}
                            </NavLink>
                          </li>
                        )}

                        {privilegeCodes.find(
                          (item) =>
                            item === PRIVILEGE_CODES.OWNER_MANAGEMENT ||
                            item === PRIVILEGE_CODES.TOPUP_BASIC
                        ) && (
                          <li>
                            <NavLink to={MODULE_ROUTE.RECHARGE_HISTORY}>
                              {STRING_SUB_MENU.RECHARGE_HISTORY}
                            </NavLink>
                          </li>
                        )}
                      </ul>
                    </AnimateHeight>
                  </li>
                </>
              )}

              {privilegeCodes.find(
                (item) =>
                  item === PRIVILEGE_CODES.OWNER_MANAGEMENT ||
                  item === PRIVILEGE_CODES.PROMOTION_COMBO_MANAGEMENT ||
                  item === PRIVILEGE_CODES.PROMOTION_TICKET_MANAGEMENT ||
                  item ===
                    PRIVILEGE_CODES.PROMOTION_RECHARGE_PACKAGE_MANAGEMENT ||
                  item ===
                    PRIVILEGE_CODES.PROMOTION_MEMBERSHIP_LEVEL_MANAGEMENT ||
                  item ===
                    PRIVILEGE_CODES.PROMOTION_CUSTOMER_DISCOUNT_MANAGEMENT ||
                  item === PRIVILEGE_CODES.PROMOTION_AREA_DISCOUNT_MANAGEMENT ||
                  item === PRIVILEGE_CODES.PROMOTION_CONFIRM ||
                  item === PRIVILEGE_CODES.PROMOTION_CREDIT_MANAGEMENT
              ) && (
                <>
                  {(showPromotionSubMenuConfig ||
                    privilegeCodes.find(
                      (item) => item === PRIVILEGE_CODES.PROMOTION_CONFIRM
                    )) && (
                    privilegeCodes.find((item) => item === PRIVILEGE_CODES.OWNER_MANAGEMENT) ||
                    privilegeCodes.find((item) => item === PRIVILEGE_CODES.PROMOTION_CONFIRM) ||
                    privilegeCodes.find((item) => item === PRIVILEGE_CODES.PROMOTION_CREDIT_MANAGEMENT)) && (
                    <li className="menu nav-item">
                      <button
                        type="button"
                        className={`${
                          currentMenu === STRING_MENU.MANAGEMENT_PROMOTION
                            ? "active"
                            : ""
                        } nav-link group w-full`}
                        onClick={() =>
                          toggleMenu(STRING_MENU.MANAGEMENT_PROMOTION)
                        }
                      >
                        <MenuItem
                          menu={STRING_MENU.MANAGEMENT_PROMOTION}
                          icon={IconTicket}
                          subMenu={currentMenu}
                        />
                        <RotatingIcon
                          menu={STRING_MENU.MANAGEMENT_PROMOTION}
                          currentMenu={currentMenu}
                          iconType="single"
                        />
                      </button>

                      <AnimateHeight
                        duration={300}
                        height={
                          currentMenu === STRING_MENU.MANAGEMENT_PROMOTION
                            ? "auto"
                            : 0
                        }
                      >
                        <ul className="text-gray-500 sub-menu">
                          {/* Chiến dịch khuyến mãi mới */}
                          {privilegeCodes.find(
                            (item) =>
                              item === PRIVILEGE_CODES.OWNER_MANAGEMENT ||
                              item ===
                                PRIVILEGE_CODES.PROMOTION_MASTER_MANAGEMENT
                          ) && (
                            <li>
                              <NavLink to={MODULE_ROUTE.PROMOTION_MASTERS}>
                                {STRING_SUB_MENU.PROMOTION_MASTERS}
                              </NavLink>
                            </li>
                          )}
                          {/* Mẫu Voucher */}
                          {privilegeCodes.find(
                            (item) =>
                              item === PRIVILEGE_CODES.OWNER_MANAGEMENT ||
                              item === PRIVILEGE_CODES.VOUCHER_MANAGEMENT
                          ) && (
                            <li>
                              <NavLink to={MODULE_ROUTE.VOUCHER_TEMPLATES}>
                                {STRING_SUB_MENU.VOUCHER_TEMPLATES}
                              </NavLink>
                            </li>
                          )}
                          {/* Danh sách Voucher */}
                          {privilegeCodes.find(
                            (item) =>
                              item === PRIVILEGE_CODES.OWNER_MANAGEMENT ||
                              item === PRIVILEGE_CODES.VOUCHER_MANAGEMENT
                          ) && (
                            <li>
                              <NavLink to={MODULE_ROUTE.VOUCHERS}>
                                {STRING_SUB_MENU.VOUCHERS}
                              </NavLink>
                            </li>
                          )}
                          {/* Báo cáo Khuyến Mãi */}
                          {/* {privilegeCodes.find(
                            (item) =>
                              item === PRIVILEGE_CODES.OWNER_MANAGEMENT ||
                              item === PRIVILEGE_CODES.REPORT_MANAGEMENT
                          ) && (
                            <li>
                              <NavLink to={MODULE_ROUTE.PROMOTION_REPORTS}>
                                {STRING_SUB_MENU.PROMOTION_REPORTS}
                              </NavLink>
                            </li>
                          )} */}
                          {/* xác nhận */}
                          {privilegeCodes.find(
                            (item) =>
                              item === PRIVILEGE_CODES.OWNER_MANAGEMENT ||
                              item ===
                                PRIVILEGE_CODES.PROMOTION_COMBO_MANAGEMENT ||
                              item === PRIVILEGE_CODES.PROMOTION_CONFIRM
                          ) && (
                            <li>
                              <NavLink to={MODULE_ROUTE.PROMOTION_BUY_HISTORY}>
                                {STRING_SUB_MENU.LIST_PROMOTION_BUY_HISTORY}
                              </NavLink>
                            </li>
                          )}
                          {privilegeCodes.find(
                            (item) =>
                              item === PRIVILEGE_CODES.OWNER_MANAGEMENT ||
                              item === PRIVILEGE_CODES.PROMOTION_CREDIT_MANAGEMENT
                          ) && (
                            <li>
                              <NavLink to={MODULE_ROUTE.PROMOTION_CREDIT_PAGE}>
                                {STRING_SUB_MENU.LIST_PROMOTION_CREDIT}
                              </NavLink>
                            </li>
                          )}
                          {renderPromotionSubMenu()}
                        </ul>
                      </AnimateHeight>
                    </li>
                  )}
                </>
              )}

              {/* Quản lý chi nhánh */}
              {privilegeCodes.find(
                (item) =>
                  item === PRIVILEGE_CODES.OWNER_MANAGEMENT ||
                  item === PRIVILEGE_CODES.BRANCH_MANAGEMENT
              ) && (
                <li className="nav-item">
                  <NavLink
                    to={MODULE_ROUTE.BRANCH}
                    className="w-full nav-link group "
                    onClick={() => toggleMenu(STRING_MENU.MANAGEMENT_BRANCH)}
                  >
                    <MenuItem
                      menu={STRING_MENU.MANAGEMENT_BRANCH}
                      icon={IconBranch}
                      subMenu={currentMenu}
                    />
                  </NavLink>
                </li>
              )}

              {/* Quản lý nhân viên */}
              {privilegeCodes.find(
                (item) =>
                  item === PRIVILEGE_CODES.OWNER_MANAGEMENT ||
                  item === PRIVILEGE_CODES.EMPLOYEE_MANAGEMENT_BASIC ||
                  item === PRIVILEGE_CODES.EMPLOYEE_DEPARTMENT_MANAGEMENT ||
                  item === PRIVILEGE_CODES.EMPLOYEE_PRIVILEGE_ASSIGNMENT ||
                  item === PRIVILEGE_CODES.SHIFT_SESSION_BASIC ||
                  item === PRIVILEGE_CODES.ATTENDANCES_BASIC ||
                  item === PRIVILEGE_CODES.SHIFT_BASIC
              ) && (
                <li className="menu nav-item">
                  <button
                    type="button"
                    className={`${
                      currentMenu === STRING_MENU.MANAGEMENT_EMPLOYEE
                        ? "active"
                        : ""
                    } nav-link group w-full`}
                    onClick={() => toggleMenu(STRING_MENU.MANAGEMENT_EMPLOYEE)}
                  >
                    <MenuItem
                      menu={STRING_MENU.MANAGEMENT_EMPLOYEE}
                      icon={IconEmployee}
                      subMenu={currentMenu}
                    />
                    <RotatingIcon
                      menu={STRING_MENU.MANAGEMENT_EMPLOYEE}
                      currentMenu={currentMenu}
                      iconType="single"
                    />
                  </button>

                  <AnimateHeight
                    duration={300}
                    height={
                      currentMenu === STRING_MENU.MANAGEMENT_EMPLOYEE
                        ? "auto"
                        : 0
                    }
                  >
                    <ul className="text-gray-500 sub-menu">
                      {privilegeCodes.find(
                        (item) =>
                          item === PRIVILEGE_CODES.OWNER_MANAGEMENT ||
                          item === PRIVILEGE_CODES.EMPLOYEE_MANAGEMENT_BASIC
                      ) && (
                        <li>
                          <NavLink to={MODULE_ROUTE.EMPLOYEE}>
                            {STRING_SUB_MENU.LIST_EMPLOYEE}
                          </NavLink>
                        </li>
                      )}

                      {privilegeCodes.find(
                        (item) =>
                          item === PRIVILEGE_CODES.OWNER_MANAGEMENT ||
                          item ===
                            PRIVILEGE_CODES.EMPLOYEE_DEPARTMENT_MANAGEMENT
                      ) && (
                        <li>
                          <NavLink to={MODULE_ROUTE.LIST_DEPARTMENT}>
                            {STRING_SUB_MENU.LIST_DEPARTMENT}
                          </NavLink>
                        </li>
                      )}

                      {/* <li>
                      <NavLink to="/finance">{STRING_SUB_MENU.PERMISSIONS}</NavLink>
                    </li> */}

                      {privilegeCodes.find(
                        (item) =>
                          item === PRIVILEGE_CODES.OWNER_MANAGEMENT ||
                          item === PRIVILEGE_CODES.SHIFT_SESSION_BASIC
                      ) && (
                        <li>
                          <NavLink to={MODULE_ROUTE.SHIFT_SESSION}>
                            {STRING_SUB_MENU.OPEN_CLOSE_SHIFT}
                          </NavLink>
                        </li>
                      )}
                      {privilegeCodes.find(
                        (item) =>
                          item === PRIVILEGE_CODES.OWNER_MANAGEMENT ||
                          item === PRIVILEGE_CODES.ATTENDANCES_BASIC
                      ) && (
                        <li>
                          <NavLink to={MODULE_ROUTE.TIMEKEEPING_USER_PAGE}>
                            {STRING_SUB_MENU.LIST_ATTENDANCE}
                          </NavLink>
                        </li>
                      )}
                      {privilegeCodes.find(
                        (item) =>
                          item === PRIVILEGE_CODES.OWNER_MANAGEMENT ||
                          item === PRIVILEGE_CODES.SHIFT_SESSION_BASIC
                      ) && (
                        <li>
                          <NavLink to={MODULE_ROUTE.SHIFT_PAGE}>
                            {STRING_SUB_MENU.LIST_SHIFT}
                          </NavLink>
                        </li>
                      )}
                    </ul>
                  </AnimateHeight>
                </li>
              )}
              {/* Quản lý khu máy trạm */}
              {privilegeCodes.find(
                (item) =>
                  item === PRIVILEGE_CODES.OWNER_MANAGEMENT ||
                  item === PRIVILEGE_CODES.AREA_BASIC
              ) && (
                <li className="nav-item">
                  <NavLink
                    to={MODULE_ROUTE.WORKSTATION_AREA}
                    className="w-full nav-link group"
                    onClick={() => toggleMenu(STRING_MENU.WORKSTATION_AREA)}
                  >
                    <MenuItem
                      menu={STRING_MENU.WORKSTATION_AREA}
                      icon={IconPcDisplay}
                      subMenu={currentMenu}
                    />
                  </NavLink>
                </li>
              )}

              {/* Quản lý sản phẩm */}
              {privilegeCodes.find(
                (item) =>
                  item === PRIVILEGE_CODES.OWNER_MANAGEMENT ||
                  item === PRIVILEGE_CODES.PRODUCT_MATERIAL_MANAGEMENT
              ) && (
                <li className="nav-item">
                  <NavLink
                    to={MODULE_ROUTE.PRODUCT}
                    className="w-full nav-link group"
                    onClick={() => toggleMenu(STRING_MENU.PRODUCT)}
                  >
                    <MenuItem
                      menu={STRING_MENU.PRODUCT}
                      icon={IconBooking}
                      subMenu={currentMenu}
                    />
                  </NavLink>
                </li>
              )}
              {/* Quản lý kho */}
              {privilegeCodes.find(
                (item) =>
                  item === PRIVILEGE_CODES.OWNER_MANAGEMENT ||
                  item === PRIVILEGE_CODES.WAREHOUSE_BASIC ||
                  item === PRIVILEGE_CODES.INVENTORY_BASIC ||
                  item === PRIVILEGE_CODES.RECEIPT_PAYMENT_BASIC ||
                  item === PRIVILEGE_CODES.RECEIPT_PAYMENT_CATEGORY_MANAGEMENT
              ) && (
                <li className="menu nav-item">
                  <button
                    type="button"
                    className={`${
                      currentMenu === STRING_MENU.MANAGEMENT_WAREHOUSING
                        ? "active"
                        : ""
                    } nav-link group w-full`}
                    onClick={() =>
                      toggleMenu(STRING_MENU.MANAGEMENT_WAREHOUSING)
                    }
                  >
                    <MenuItem
                      menu={STRING_MENU.MANAGEMENT_WAREHOUSING}
                      icon={IconCashCoin}
                      subMenu={currentMenu}
                    />
                    <RotatingIcon
                      menu={STRING_MENU.MANAGEMENT_WAREHOUSING}
                      currentMenu={currentMenu}
                      iconType="single"
                    />
                  </button>

                  <AnimateHeight
                    duration={300}
                    height={
                      currentMenu === STRING_MENU.MANAGEMENT_WAREHOUSING
                        ? "auto"
                        : 0
                    }
                  >
                    <ul className="text-gray-500 sub-menu">
                      {privilegeCodes.find(
                        (item) =>
                          item === PRIVILEGE_CODES.OWNER_MANAGEMENT ||
                          item === PRIVILEGE_CODES.PRODUCT_MATERIAL_MANAGEMENT
                      ) && (
                        <li>
                          <NavLink to={MODULE_ROUTE.MATERIAL_AND_PRODUCT}>
                            {STRING_SUB_MENU.MATERIAL_AND_PRODUCT}
                          </NavLink>
                        </li>
                      )}

                      {privilegeCodes.find(
                        (item) =>
                          item === PRIVILEGE_CODES.OWNER_MANAGEMENT ||
                          item === PRIVILEGE_CODES.WAREHOUSE_BASIC
                      ) && (
                        <li>
                          <NavLink to={MODULE_ROUTE.WAREHOUSING}>
                            {STRING_SUB_MENU.WAREHOUSING}
                          </NavLink>
                        </li>
                      )}

                      {privilegeCodes.find(
                        (item) =>
                          item === PRIVILEGE_CODES.OWNER_MANAGEMENT ||
                          item === PRIVILEGE_CODES.RECEIPT_PAYMENT_BASIC
                      ) && (
                        <li>
                          <NavLink to={MODULE_ROUTE.INCOME_AND_EXPENDITURE}>
                            {STRING_SUB_MENU.INCOME_AND_EXPENDITURE}
                          </NavLink>
                        </li>
                      )}

                      {privilegeCodes.find(
                        (item) =>
                          item === PRIVILEGE_CODES.OWNER_MANAGEMENT ||
                          item ===
                            PRIVILEGE_CODES.RECEIPT_PAYMENT_CATEGORY_MANAGEMENT
                      ) && (
                        <li>
                          <NavLink to={MODULE_ROUTE.CATEGORIES_RECEIPT_PAGE}>
                            {STRING_SUB_MENU.CATEGORIES_RECEIPT}
                          </NavLink>
                        </li>
                      )}

                      {privilegeCodes.find(
                        (item) =>
                          item === PRIVILEGE_CODES.OWNER_MANAGEMENT ||
                          item === PRIVILEGE_CODES.INVENTORY_BASIC
                      ) && (
                        <li>
                          <NavLink to={MODULE_ROUTE.INVENTORY_WAREHOUSE}>
                            {STRING_SUB_MENU.INVENTORY_WAREHOUSE}
                          </NavLink>
                        </li>
                      )}
                    </ul>
                  </AnimateHeight>
                </li>
              )}

              {privilegeCodes.find(
                (item) =>
                  item === PRIVILEGE_CODES.OWNER_MANAGEMENT ||
                  item === PRIVILEGE_CODES.ORDER_MANAGEMENT ||
                  item === PRIVILEGE_CODES.ORDER_PENDING ||
                  item === PRIVILEGE_CODES.ORDER_CONFIRM ||
                  item === PRIVILEGE_CODES.ORDER_COMPLETED ||
                  item === PRIVILEGE_CODES.ORDER_DONE ||
                  item === PRIVILEGE_CODES.ORDER_CANCEL
              ) && (
                <li className="nav-item">
                  <NavLink
                    to={MODULE_ROUTE.ORDER}
                    className="w-full nav-link group"
                    onClick={() => toggleMenu(STRING_MENU.MANAGEMENT_ORDER)}
                  >
                    <MenuItem
                      menu={STRING_MENU.MANAGEMENT_ORDER}
                      icon={IconShop}
                      subMenu={currentMenu}
                    />
                  </NavLink>
                </li>
              )}

              <li className="nav-item">
                <NavLink
                  to={MODULE_ROUTE.PRINTER_PAGE}
                  className="w-full nav-link group"
                  onClick={() => toggleMenu(STRING_MENU.PRINTER)}
                >
                  <MenuItem
                    menu={STRING_MENU.PRINTER}
                    icon={IconPrinter}
                    subMenu={currentMenu}
                  />
                </NavLink>
              </li>
              {/* Quản lý booking */}
              {privilegeCodes.find(
                (item) =>
                  item === PRIVILEGE_CODES.OWNER_MANAGEMENT ||
                  item === PRIVILEGE_CODES.BOOKING_MANAGEMENT
              ) && (
                <li className="nav-item">
                  <NavLink
                    to={MODULE_ROUTE.BOOKING_MANAGEMENT}
                    className="w-full nav-link group"
                    onClick={() => toggleMenu(STRING_MENU.MANAGEMENT_BOOKING)}
                  >
                    <MenuItem
                      menu={STRING_MENU.MANAGEMENT_BOOKING}
                      icon={IconBooking}
                      subMenu={currentMenu}
                    />
                  </NavLink>
                </li>
              )}
              {/* Quản lý nạp tiền */}
              {privilegeCodes.find(
                (item) =>
                  item === PRIVILEGE_CODES.OWNER_MANAGEMENT ||
                  item === PRIVILEGE_CODES.RECHARGE_PACKAGE_MANAGEMENT
              ) && (
                <li className="nav-item">
                  <NavLink
                    to={MODULE_ROUTE.RECHARGE_PACKAGE}
                    className="w-full nav-link group"
                    onClick={() =>
                      toggleMenu(STRING_MENU.MANAGEMENT_RECHARGE_PACKAGE)
                    }
                  >
                    <MenuItem
                      menu={STRING_MENU.MANAGEMENT_RECHARGE_PACKAGE}
                      icon={IconShop}
                      subMenu={currentMenu}
                    />
                  </NavLink>
                </li>
              )}
              {/* Quản lý gói giờ chơi */}
              {privilegeCodes.find(
                (item) =>
                  item === PRIVILEGE_CODES.OWNER_MANAGEMENT ||
                  item === PRIVILEGE_CODES.RECHARGE_PACKAGE_MANAGEMENT
              ) && (
                <li className="menu nav-item">
                  <button
                    type="button"
                    className={`${
                      currentMenu === STRING_MENU.MANAGEMENT_PLAY_TIME_PACKAGE
                        ? "active"
                        : ""
                    } nav-link group w-full`}
                    onClick={() =>
                      toggleMenu(STRING_MENU.MANAGEMENT_PLAY_TIME_PACKAGE)
                    }
                  >
                    <MenuItem
                      menu={STRING_MENU.MANAGEMENT_PLAY_TIME_PACKAGE}
                      icon={IconCashCoin}
                      subMenu={currentMenu}
                    />
                    <RotatingIcon
                      menu={STRING_MENU.MANAGEMENT_PLAY_TIME_PACKAGE}
                      currentMenu={currentMenu}
                      iconType="single"
                    />
                  </button>

                  <AnimateHeight
                    duration={300}
                    height={
                      currentMenu === STRING_MENU.MANAGEMENT_PLAY_TIME_PACKAGE
                        ? "auto"
                        : 0
                    }
                  >
                    <ul className="text-gray-500 sub-menu">
                      <li>
                        <NavLink to={MODULE_ROUTE.PLAY_TIME_PACKAGE}>
                          {STRING_SUB_MENU.LIST_PLAY_TIME_PACKAGE}
                        </NavLink>
                      </li>
                      <li>
                        <NavLink to={MODULE_ROUTE.PLAY_TIME_TRANSACTIONS}>
                          {STRING_SUB_MENU.PLAY_TIME_TRANSACTIONS}
                        </NavLink>
                      </li>
                    </ul>
                  </AnimateHeight>
                </li>
              )}
              {/* Quản lý tin tức */}
              {privilegeCodes.find(
                (item) =>
                  item === PRIVILEGE_CODES.OWNER_MANAGEMENT ||
                  item === PRIVILEGE_CODES.NEWS_MANAGEMENT
              ) && (
                <li className="nav-item">
                  <NavLink
                    to={MODULE_ROUTE.MANAGEMENT_NEWFEED}
                    className="w-full nav-link group"
                    onClick={() => toggleMenu(STRING_MENU.MANAGEMENT_NEWFEED)}
                  >
                    <MenuItem
                      menu={STRING_MENU.MANAGEMENT_NEWFEED}
                      icon={IconNewFeed}
                      subMenu={currentMenu}
                    />
                  </NavLink>
                </li>
              )}

              {privilegeCodes.find(
                (item) =>
                  item === PRIVILEGE_CODES.OWNER_MANAGEMENT ||
                  item === PRIVILEGE_CODES.GAME_MANAGEMENT
              ) && (
                <li className="nav-item">
                  <NavLink
                    to={MODULE_ROUTE.MANAGEMENT_GAME}
                    className="w-full nav-link group"
                    onClick={() => toggleMenu(STRING_MENU.MANAGEMENT_GAME)}
                  >
                    <MenuItem
                      menu={STRING_MENU.MANAGEMENT_GAME}
                      icon={IconGame}
                      subMenu={currentMenu}
                    />
                  </NavLink>
                </li>
              )}

              {/* Quản lý background */}
              <li className="nav-item">
                <NavLink
                  to={MODULE_ROUTE.BACKGROUND_LOGIN}
                  className="w-full nav-link group"
                  onClick={() => toggleMenu(STRING_MENU.BACKGROUND_LOGIN)}
                >
                  <MenuItem
                    menu={STRING_MENU.BACKGROUND_LOGIN}
                    icon={IconBackground}
                    subMenu={currentMenu}
                  />
                </NavLink>
              </li>

              {/* Quản lý hệ thống */}
              {privilegeCodes.find(
                (item) =>
                  item === PRIVILEGE_CODES.OWNER_MANAGEMENT ||
                  item === PRIVILEGE_CODES.API_KEY_MANAGEMENT
              ) && (
                <li className="nav-item">
                  <NavLink
                    to={MODULE_ROUTE.API_KEY}
                    className="w-full nav-link group"
                    onClick={() => toggleMenu(STRING_MENU.API_KEY)}
                  >
                    <MenuItem
                      menu={STRING_MENU.API_KEY}
                      icon={IconApiKey}
                      subMenu={currentMenu}
                    />
                  </NavLink>
                </li>
              )}
              {/* Quản lý lịch sử hoạt động */}
              {privilegeCodes.find(
                (item) =>
                  item === PRIVILEGE_CODES.OWNER_MANAGEMENT ||
                  item === PRIVILEGE_CODES.ACTIVITY_LOG_VIEW
              ) && (
                <li className="nav-item">
                  <NavLink
                    to={MODULE_ROUTE.TIMELINE}
                    className="w-full nav-link group"
                    onClick={() => toggleMenu(STRING_MENU.TIMELINE)}
                  >
                    <MenuItem
                      menu={STRING_MENU.TIMELINE}
                      icon={IconHistory}
                      subMenu={currentMenu}
                    />
                  </NavLink>
                </li>
              )}
              {/* Quản lý hệ thống */}
              {privilegeCodes.find(
                (item) =>
                  item === PRIVILEGE_CODES.OWNER_MANAGEMENT ||
                  item === PRIVILEGE_CODES.SYSTEM_SETTINGS
              ) && (
                <li className="nav-item">
                  <NavLink
                    to={MODULE_ROUTE.SETTINGS}
                    className="w-full nav-link group"
                    onClick={() => toggleMenu(STRING_MENU.SETTING)}
                  >
                    <MenuItem
                      menu={STRING_MENU.SETTING}
                      icon={IcGeneralSettings}
                      subMenu={currentMenu}
                    />
                  </NavLink>
                </li>
              )}
            </ul>
          </PerfectScrollbar>
        </div>
      </nav>
    </div>
  );
};

export default Sidebar;
